import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, CircularProgress, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import {
  AllocationTargetsService,
  type AllocationTarget,
  type DriftRow,
} from '../../services/allocation-targets-service';
import { baseMoney } from '../../stores/currency-store';

interface Props {
  targets: AllocationTarget[];
}

/** Bar width scale: ±25% drift fills the bar, beyond clamps. Chosen so the
 * common few-percent drifts remain visible instead of hairlines. */
const DRIFT_FULL_SCALE = 25;

/**
 * Actual vs target per category, with the gap made visual.
 *
 * Positive drift (overweight) draws right, negative left, so the table reads
 * like a tug-of-war diagram: which side of each category is winning.
 */
export const DriftTab: React.FC<Props> = ({ targets }) => {
  const [targetId, setTargetId] = useState<string>(targets[0]?.id ?? '');
  const [rows, setRows] = useState<DriftRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the selection valid when targets change (e.g. one was deleted).
  useEffect(() => {
    if (!targets.some((t) => t.id === targetId)) {
      setTargetId(targets[0]?.id ?? '');
    }
  }, [targets, targetId]);

  useEffect(() => {
    if (!targetId) { setRows(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    AllocationTargetsService.drift(targetId)
      .then((r) => { if (!cancelled) setRows(r.drift); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'Failed to compute drift'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [targetId]);

  if (targets.length === 0) {
    return (
      <Alert severity="info">
        Create a target first — drift is measured against it.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Target" value={targetId}
          onChange={(e) => setTargetId(e.target.value)} sx={{ minWidth: 220 }}
        >
          {targets.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        {loading && <CircularProgress size={20} />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows && (
        <Card>
          <CardContent sx={{ pb: 1 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="right">Target</TableCell>
                    <TableCell align="right">Drift</TableCell>
                    <TableCell sx={{ width: '30%' }}>Over / under</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const drift = Number(r.drift_pct);
                    const over = drift > 0;
                    const width = Math.min(Math.abs(drift) / DRIFT_FULL_SCALE, 1) * 50;
                    return (
                      <TableRow key={r.category} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={650}>{r.category}</Typography>
                        </TableCell>
                        <TableCell align="right" className="fw-num">
                          {baseMoney(r.actual_value)}
                        </TableCell>
                        <TableCell align="right" className="fw-num">{r.actual_pct}%</TableCell>
                        <TableCell align="right" className="fw-num">{r.target_pct}%</TableCell>
                        <TableCell
                          align="right" className="fw-num"
                          sx={{ color: over ? 'var(--outflow-text)' : 'var(--inflow-text)' }}
                        >
                          {over ? '+' : ''}{r.drift_pct}%
                        </TableCell>
                        <TableCell>
                          {/* Diverging bar: centre line = on target. */}
                          <Box sx={{ position: 'relative', height: 10, borderRadius: 5, bgcolor: 'action.hover' }}>
                            <Box sx={{
                              position: 'absolute', left: '50%', top: 0, bottom: 0,
                              width: 1, bgcolor: 'divider',
                            }} />
                            <Box sx={{
                              position: 'absolute', top: 0, bottom: 0,
                              left: over ? '50%' : `${50 - width}%`,
                              width: `${width}%`,
                              borderRadius: 5,
                              bgcolor: over ? 'var(--outflow-text)' : 'var(--inflow-text)',
                              opacity: 0.75,
                            }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 1 }}>
              Drift = actual − target. Unclassified holdings appear as their own
              row; classify them for an honest picture. Descriptive only — not
              advice.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
